'use server';

/**
 * The wizard's S1 (pasted text / classic + «replace hero»): parse the source
 * before the order and hand back the cast, so the parent picks whom each hero
 * replaces. The parse travels in the order (params.preParsed) — the run's s1
 * echoes it, the entity ids stay the same.
 */
import { SessionService } from '@/contexts/authentication';
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';
import type { ActionResult } from '@/shared/types/actions';
import type { ParsedCastMember } from '../story/preParse';
import type { ParsedSource } from '../story/types';

export async function preParseSource(input: {
  text?: string;
  /** a classic from the corpus (its stored parse is reused when there is one) */
  basisStoryId?: string;
  language: string;
  ageGroup?: string;
  title?: string;
}): Promise<ActionResult<{ parsed: ParsedSource | null; cast: ParsedCastMember[] }>> {
  try {
    const familyId = await SessionService.getFamilyId();
    if (!familyId) return { success: false, error: 'Not authenticated' };
    const { preParseText, castOf } = await import('../story/preParse');
    const { runLlmGenerate } = await import('@/shared/infrastructure/workflows/llmRun');
    const runLlm = async (call: { messages: Array<{ role: string; content: string }>; response_schema: unknown }) => {
      const out = await runLlmGenerate({
        startKey: `pv2-preparse:${familyId}:${crypto.randomUUID()}`,
        messages: call.messages,
        responseSchema: call.response_schema as Record<string, unknown>,
        familyId,
        timeoutMs: 120_000,
      });
      return out.json ?? JSON.parse(out.text);
    };
    if (input.basisStoryId) {
      // A classic / public basis: its text is never shipped to the client. It is
      // parsed once and stored on basis_texts (the run's `load` reads the same
      // parse, so the entity ids match); the wizard gets the cast only.
      const { StoryV2Repository } = await import('../story/StoryV2Repository');
      const repo = new StoryV2Repository();
      const stored = await repo.loadClassicParse(input.basisStoryId).catch(() => null);
      if (stored) return { success: true, data: { parsed: null, cast: castOf(stored) } };
      const { getPublishingRepository } = await import('@/contexts/publishing/repositories/PublishingRepository');
      const basis = await getPublishingRepository().getBasisText(input.basisStoryId);
      if (!basis?.text?.trim()) return { success: false, error: 'Not found' };
      const parsed = await preParseText({ text: basis.text, language: input.language, ageGroup: input.ageGroup, title: input.title }, runLlm);
      await repo.saveClassicParse(input.basisStoryId, parsed.parsed);
      return { success: true, data: { parsed: null, cast: parsed.cast } };
    }
    const text = input.text ?? '';
    if (!text.trim()) return { success: false, error: 'Text is required' };
    const result = await preParseText({ text, language: input.language, ageGroup: input.ageGroup, title: input.title }, runLlm);
    return { success: true, data: result };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('preParseSource', e) };
  }
}
